import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    UserPlus, TreePine, Camera, Download, Loader2, RotateCcw, Share2,
    PhoneOff, ShieldOff, UserPlus2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "@/contexts/LanguageContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useSiteAsset } from "@/lib/useSiteAsset";

/**
 * "Try without signup" — build a real family tree from optional relatives.
 * Only "Your Name" is required; every other relative is optional and skipped
 * if left blank. The tree is rendered as pure DOM (no external images) so the
 * PNG download is always reliable. All state is local — nothing is saved.
 */

const initials = (name) => {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// gen 0 = oldest (top). side L/R positions couples; children have no side.
const FIELDS = [
    { key: "greatgrandfather", label: "Great-grandfather", role: "Great-grandfather", gen: 0, color: "#8B6D3A" },
    { key: "greatgrandmother", label: "Great-grandmother", role: "Great-grandmother", gen: 0, color: "#B08968" },
    { key: "grandfather",      label: "Grandfather",       role: "Grandfather",       gen: 1, color: "#C9A55B" },
    { key: "grandmother",      label: "Grandmother",       role: "Grandmother",       gen: 1, color: "#D9B36B" },
    { key: "father",           label: "Father",            role: "Father",            gen: 2, color: "#EF4444" },
    { key: "mother",           label: "Mother",            role: "Mother",            gen: 2, color: "#F472B6" },
    { key: "you",              label: "Your Name",         role: "You",               gen: 3, color: "#6D28D9", required: true },
    { key: "spouse",           label: "Spouse (Wife / Husband)", role: "Spouse",       gen: 3, color: "#7C3AED" },
    { key: "son",              label: "Son",               role: "Son",               gen: 4, color: "#0EA5E9" },
    { key: "daughter",         label: "Daughter",          role: "Daughter",          gen: 4, color: "#14B8A6" },
    { key: "grandchild",       label: "Grandchild",        role: "Grandchild",        gen: 5, color: "#8B5CF6" },
];

// Form is grouped into intuitive sections (elder-friendly).
const FORM_GROUPS = [
    { title: "You", keys: ["you", "spouse"] },
    { title: "Parents", keys: ["father", "mother"] },
    { title: "Grandparents", keys: ["grandfather", "grandmother"] },
    { title: "Great-grandparents", keys: ["greatgrandfather", "greatgrandmother"] },
    { title: "Children", keys: ["son", "daughter"] },
    { title: "Grandchildren", keys: ["grandchild"] },
];

const GEN_LABEL = ["Great-grandparents", "Grandparents", "Parents", "You & Spouse", "Children", "Grandchildren"];

const FAMILY_BACKDROP =
    "https://images.unsplash.com/photo-1780329943665-ac2d6784e956?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

// ── One person node card (pure DOM → download-safe) ─────────────────────────
function PersonNode({ name, role, color, photo, testid }) {
    return (
        <div className="flex flex-col items-center w-[112px] sm:w-[128px]" data-testid={testid}>
            <div
                className="h-14 w-14 sm:h-16 sm:w-16 rounded-full flex items-center justify-center overflow-hidden shadow-md border-2 border-white"
                style={{ background: color }}
            >
                {photo ? (
                    <img src={photo} alt={name} className="h-full w-full object-cover" />
                ) : (
                    <span className="text-white text-[16px] sm:text-[18px] font-bold">{initials(name)}</span>
                )}
            </div>
            <div className="mt-1.5 text-center leading-tight">
                <div className="text-[12px] sm:text-[13px] font-bold text-[#1F2937] truncate max-w-[112px] sm:max-w-[128px]">{name}</div>
                <div className="text-[10px] sm:text-[10.5px] font-semibold uppercase tracking-wide" style={{ color }}>{role}</div>
            </div>
        </div>
    );
}

// ── Rendered family tree (used both on-screen and for the PNG) ──────────────
function FamilyTreeGraphic({ values, photos, familyTitle, brand }) {
    // Build a list of generations that actually have people.
    const gens = [];
    for (let g = 0; g <= 5; g++) {
        const people = FIELDS.filter((f) => f.gen === g)
            .map((f) => ({ ...f, name: (values[f.key] || "").trim() }))
            .filter((p) => p.name);
        if (people.length) gens.push({ g, people });
    }

    return (
        <div className="w-full flex flex-col items-center">
            {familyTitle && (
                <div className="text-[18px] sm:text-[22px] font-extrabold text-[#4C1D95] mb-1" data-testid="demo-poster-family-title">
                    {familyTitle}
                </div>
            )}
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#8B6D3A] mb-4">
                {brand} · Family Tree
            </div>

            <div className="flex flex-col items-center gap-0">
                {gens.map((gen, gi) => (
                    <div key={gen.g} className="flex flex-col items-center">
                        <div className="text-[9.5px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1">
                            {GEN_LABEL[gen.g]}
                        </div>
                        <div className="flex items-start justify-center gap-5 sm:gap-8">
                            {gen.people.map((p) => (
                                <PersonNode
                                    key={p.key}
                                    name={p.name}
                                    role={p.role}
                                    color={p.color}
                                    photo={photos[p.key] || ""}
                                    testid={`demo-node-${p.key}`}
                                />
                            ))}
                        </div>
                        {gi < gens.length - 1 && (
                            <div className="h-6 w-[2px] bg-[#C9A55B] my-1" aria-hidden="true" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Result screen: poster preview + actions ─────────────────────────────────
function TreePreview({ values, photos, onRestart }) {
    const { t } = useLang();
    const branding = useBranding();
    const posterRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [sharing, setSharing] = useState(false);
    const brand = branding.app_name || "Family Repository";

    const youName = (values.you || "").trim();
    const nameParts = youName.split(/\s+/).filter(Boolean);
    const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : youName;
    const familyTitle = surname ? `The ${surname} Family` : "";

    const buildPng = async () => {
        // Pure-DOM poster with a solid/gradient background and data-URL photos
        // only → no cross-origin taint, so toPng is reliable.
        return toPng(posterRef.current, {
            pixelRatio: 2,
            cacheBust: true,
            backgroundColor: "#FFFDF6",
        });
    };

    const downloadPoster = async () => {
        if (!posterRef.current || downloading) return;
        setDownloading(true);
        try {
            const dataUrl = await buildPng();
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `${surname || "family"}-family-tree.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("poster download failed", e);
        } finally {
            setDownloading(false);
        }
    };

    const sharePoster = async () => {
        if (!posterRef.current || sharing) return;
        setSharing(true);
        try {
            const dataUrl = await buildPng();
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], "family-tree.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: familyTitle || "My Family Tree" });
            } else {
                const wa = `https://wa.me/?text=${encodeURIComponent(`${familyTitle || "My family tree"} — made free on ${brand}. Start yours: ${window.location.origin}/login`)}`;
                window.open(wa, "_blank");
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("share failed", e);
        } finally {
            setSharing(false);
        }
    };

    return (
        <div className="space-y-5">
            {/* Poster frame — captured for the PNG */}
            <div
                ref={posterRef}
                data-testid="demo-poster-frame"
                className="relative rounded-2xl border-4 border-[#C9A55B] bg-[#FFFDF6] p-5 sm:p-8 overflow-hidden"
                style={{ background: "linear-gradient(135deg,#FFFDF6 0%,#F6F1FF 100%)" }}
            >
                {/* diagonal brand watermark (pure text → safe) */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center opacity-[0.05] select-none" aria-hidden="true">
                    {[0, 1, 2].map((r) => (
                        <div key={r} className="whitespace-nowrap text-[38px] font-extrabold text-[#4C1D95]" style={{ transform: "rotate(-24deg)" }}>
                            {`${brand}   ${brand}   ${brand}`}
                        </div>
                    ))}
                </div>

                <div className="relative">
                    <FamilyTreeGraphic values={values} photos={photos} familyTitle={familyTitle} brand={brand} />
                    <div className="mt-6 flex items-center justify-between border-t border-[#C9A55B]/40 pt-3">
                        <div className="text-[10px] sm:text-[11px] text-[#6B7280] font-semibold">
                            Made free on {brand} — {window.location.host}
                        </div>
                        <div className="flex items-center gap-1.5" data-testid="demo-poster-qr">
                            <span className="text-[9px] text-[#6B7280] font-bold uppercase">Scan</span>
                            <QRCodeSVG value={`${window.location.origin}/login`} size={40} level="M" fgColor="#4C1D95" bgColor="#FFFFFF" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2.5">
                <Button
                    type="button"
                    onClick={downloadPoster}
                    disabled={downloading}
                    data-testid="demo-download-poster-btn"
                    className="flex-1 min-w-[150px] bg-[#1B5E20] hover:bg-[#0F3311] text-white"
                >
                    {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {downloading ? "Preparing…" : "Download family tree"}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={sharePoster}
                    disabled={sharing}
                    data-testid="demo-share-btn"
                    className="flex-1 min-w-[130px] border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                >
                    {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                    Share
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onRestart}
                    data-testid="demo-restart-btn"
                    className="text-[#6B7280] hover:text-[#6D28D9]"
                >
                    <RotateCcw className="w-4 h-4 mr-2" /> Start over
                </Button>
            </div>

            <Link to="/login" className="block" data-testid="demo-signup-link">
                <Button type="button" className="w-full bg-[#6D28D9] hover:bg-[#5B21B6] text-white">
                    <UserPlus className="w-4 h-4 mr-2" /> Save it forever — start free
                </Button>
            </Link>
        </div>
    );
}

export function TryDemoTree() {
    const { t } = useLang();
    const fileRefs = useRef({});
    const [values, setValues] = useState({});
    const [photos, setPhotos] = useState({});
    const [showTree, setShowTree] = useState(false);
    const demoBg = useSiteAsset("landing.demo_tree", FAMILY_BACKDROP);

    const TRUST_CHIPS = [
        { icon: PhoneOff,  label: t("demoTrustNoPhone") },
        { icon: ShieldOff, label: t("demoTrustNoOtp") },
        { icon: UserPlus2, label: t("demoTrustNoSignup") },
    ];

    const setValue = (key, v) => setValues((old) => ({ ...old, [key]: v }));
    const onPickPhoto = (key, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setPhotos((old) => ({ ...old, [key]: String(reader.result) }));
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const restart = () => {
        setShowTree(false);
        setValues({});
        setPhotos({});
    };

    const canGenerate = (values.you || "").trim().length > 1;
    const filledCount = FIELDS.filter((f) => (values[f.key] || "").trim()).length;

    const fieldFor = (key) => FIELDS.find((f) => f.key === key);

    return (
        <section
            id="try-demo"
            data-testid="try-demo-section"
            className="relative overflow-hidden py-4 sm:py-5 px-4 sm:px-6 lg:px-8"
        >
            <div className="absolute inset-0 -z-0" aria-hidden="true">
                <img src={demoBg.url || FAMILY_BACKDROP} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
                <div className="absolute inset-0 bg-white/88" />
                <div className="absolute inset-0 bg-gradient-to-b from-white via-white/60 to-white" />
            </div>

            <div className="relative max-w-6xl w-full mx-auto">
                {/* Header — trust chips centered on TOP, then eyebrow → heading → subline (single column, tighter) */}
                <div className="mb-3 text-center">
                    <div className="flex items-center justify-center flex-wrap gap-2 mb-2.5" data-testid="demo-trust-chips">
                        {TRUST_CHIPS.map((c) => {
                            const Icon = c.icon;
                            return (
                                <span key={c.label} className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 shadow-[0_4px_14px_rgba(17,24,39,0.10)] text-[12.5px] font-bold text-[#111827] whitespace-nowrap">
                                    <Icon className="w-4 h-4 text-[#EF4444]" />
                                    <span>{c.label}</span>
                                </span>
                            );
                        })}
                    </div>
                    <h2 className="max-w-none text-[clamp(20px,3.2vw,32px)] font-extrabold text-[#6D28D9] leading-[1.08] tracking-[-0.02em]">
                        {t("demoHeadline")}
                    </h2>
                    <p className="mt-1.5 text-[13px] sm:text-[14px] text-[#4B5563]">
                        Fill only what you know — <strong>only your name is needed</strong>. Leave the rest blank and we’ll skip it.
                    </p>
                </div>

                {/* Card */}
                <div className="relative bg-white rounded-[22px] shadow-[0_24px_60px_rgba(17,24,39,0.12)] p-4 sm:p-6" data-testid="try-demo-card">
                    {!showTree ? (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-5 gap-y-3" data-testid="demo-form">
                            {FORM_GROUPS.map((group) => (
                                <div key={group.title}>
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">{group.title}</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-3 gap-y-2">
                                        {group.keys.map((key) => {
                                            const f = fieldFor(key);
                                            const val = values[key] || "";
                                            const photo = photos[key];
                                            return (
                                                <div key={key} className="flex items-center gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <label className="text-[12px] font-semibold text-[#374151] flex items-center gap-1">
                                                            {f.label}
                                                            {f.required
                                                                ? <span className="text-[#EF4444]">*</span>
                                                                : <span className="text-[10px] font-normal text-[#9CA3AF]">(optional)</span>}
                                                        </label>
                                                        <Input
                                                            value={val}
                                                            onChange={(e) => setValue(key, e.target.value)}
                                                            placeholder={f.required ? "e.g. Rahul Sharma" : "Leave blank to skip"}
                                                            data-testid={`demo-input-${key}`}
                                                            className="mt-0.5 h-9 border-[#D1D5DB] focus:border-[#6D28D9] rounded-xl"
                                                        />
                                                    </div>
                                                    <input
                                                        ref={(el) => (fileRefs.current[key] = el)}
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(e) => onPickPhoto(key, e)}
                                                        data-testid={`demo-photo-input-${key}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => fileRefs.current[key]?.click()}
                                                        data-testid={`demo-photo-btn-${key}`}
                                                        title="Add photo (optional)"
                                                        className="mt-4 h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-xl border border-[#D1D5DB] hover:bg-[#F9FAFB] overflow-hidden"
                                                    >
                                                        {photo
                                                            ? <img src={photo} alt="" className="h-full w-full object-cover" />
                                                            : <Camera className="h-4 w-4 text-[#6B7280]" />}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            <div className="lg:col-span-3 flex items-center justify-between pt-2 border-t border-[#F0F0F0]">
                                <span className="text-[12.5px] text-[#6B7280]">{filledCount} added{!canGenerate && <span className="text-[#9CA3AF]"> · Enter your name to generate the tree.</span>}</span>
                                <Button
                                    type="button"
                                    disabled={!canGenerate}
                                    onClick={() => setShowTree(true)}
                                    data-testid="demo-generate-btn"
                                    className="bg-[#6D28D9] hover:bg-[#5B21B6] text-white rounded-full px-7 h-11 font-semibold disabled:opacity-50"
                                >
                                    <TreePine className="w-4 h-4 mr-2" /> Generate family tree
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <TreePreview values={values} photos={photos} onRestart={restart} />
                    )}
                </div>
            </div>
        </section>
    );
}

export default TryDemoTree;
